import { api } from '@alpha/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';

/**
 * Shown whenever the session reports `is_impersonating` — an admin has assumed
 * this member's session (§6.2). The colours are fixed deliberately: this banner
 * must never blend into the gold panel theme, because it is the only signal
 * that actions taken here are being performed AS someone else.
 */
export function ImpersonationBanner({
  userName,
  impersonatorName,
}: {
  userName: string;
  impersonatorName?: string | null;
}) {
  const queryClient = useQueryClient();

  const stop = useMutation({
    mutationFn: () => api.admin.stopImpersonating(),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/');
    },
  });

  return (
    <div
      role="alert"
      style={{
        background: '#7b2d00',
        border: '2px solid #e67e22',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        fontSize: '0.9rem',
        color: '#ffe3c7',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserCog size={18} />
        <strong>Impersonation active</strong>
        {impersonatorName ? `— ${impersonatorName} is viewing as ${userName}.` : `— viewing as ${userName}.`}
      </span>
      <button
        type="button"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
        style={{
          background: '#e67e22',
          color: '#0a0a0a',
          border: 'none',
          borderRadius: 6,
          padding: '6px 16px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {stop.isPending ? 'Exiting…' : 'Exit impersonation'}
      </button>
    </div>
  );
}
